import classNames from 'classnames';
import React, { useEffect, useState, useRef } from 'react';
import { cn } from '@/utils/cn';
import { DEFAULT_LANGUAGE } from '@/constants';
import { useSelector } from 'react-redux';
import Icon from '../Icon';
import Text from '../Text';
import ModalSlideButton from '../Button/ModalSlideButton';

function DropdownOptionsData({
  options = [],
  onSelectOption,
  optionValue,
  onView,
  onClose,
  inputSelectionRef,
}) {
  const { lang: selectLang } = useSelector((state) => state.globalSettings);
  // const [searchTerm, setSearchTerm] = useState('');
  // const [filteredResult, setFilteredResult] = useState(options);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(
    optionValue ?? options[0],
  );
  const [isMobile, setIsMobile] = useState(false);
  const [selectionInputRect, setSelectionInputRect] = useState('');

  const dropdownRef = useRef(null);
  const searchBarRef = useRef(null);

  useEffect(() => {
    setIsOpen(onView);

    // Check if the window is small on mount
    const checkMobileView = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobileView();
    window.addEventListener('resize', checkMobileView);

    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, [onView]);

  useEffect(() => {
    const positionDropdown = () => {
      if (inputSelectionRef.current && dropdownRef.current) {
        const tempSelectionInputRect =
          inputSelectionRef.current.getBoundingClientRect();
        setSelectionInputRect(tempSelectionInputRect);
      }
    };

    if (isOpen) {
      if (!isMobile) {
        document.body.style.overflow = '';
      }

      positionDropdown();

      if (!isMobile) {
        window.addEventListener('scroll', positionDropdown, true);
        window.addEventListener('resize', positionDropdown);

        return () => {
          window.removeEventListener('scroll', positionDropdown, true);
          window.removeEventListener('resize', positionDropdown);
        };
      }
    }
  }, [isOpen, inputSelectionRef, isMobile]);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(onClose, 100);
  };

  const handleSelectOption = (option) => {
    setSelectedOption(option);
    onSelectOption(option);
  };

  useEffect(() => {
    if (optionValue) {
      setSelectedOption(optionValue);
    }
  }, [optionValue]);

  useEffect(() => {
    if (onView) {
      setTimeout(() => setIsOpen(true), 100);
    } else {
      setIsOpen(false);
    }
  }, [onView]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        searchBarRef.current &&
        !searchBarRef.current.contains(event.target)
      ) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // const handleSearch = (value) => {
  //   const term = value;
  //   setSearchTerm(term);
  //   setFilteredResult(
  //     options.filter((option) =>
  //       option?.name?.en?.toLowerCase().includes(term.toLowerCase()),
  //     ),
  //   );
  // };

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      style={{
        top: `${selectionInputRect.height + selectionInputRect.y}px`,
        // left: `${selectionInputRect.left}px`,
        left: 'auto',
        right: '102px',
      }}
      className={cn(
        'w-full lg:max-w-[400px] h-full lg:max-h-[220px] bg-black/30 lg:bg-transparent z-[1000] invisible transition-all duration-200 fixed',
        {
          'opacity-100 visible': isOpen && !isMobile,
          'opacity-100 bottom-0 left-0 right-0': !isOpen && !isMobile,

          'opacity-100 visible !inset-0': isOpen && isMobile,
          'opacity-0 invisible bottom-0 left-0 right-0': !isOpen && isMobile,
        },
      )}
    >
      <div
        className={classNames(
          'bg-white w-full lg:w-[390px] p-20 fixed lg:static bottom-[-440px] left-0 right-0 rounded-0 lg:rounded-xl opacity-50 space-y-[16px] lg:space-y-0 transition-all duration-100 rounded-t-xl shadow-job-search-filter lg:shadow-card-hover',
          { '!bottom-0 mt-3 !opacity-100': isOpen },
        )}
      >
        <ModalSlideButton onClose={handleClose} />
        <div className="lg:flex lg:justify-between gap-[10px] relative">
          <Icon
            type="close"
            onClick={handleClose}
            className="lg:hidden absolute right-0 top-0 cursor-pointer rotate-90 size-[24px]"
          />
          <Text
            type="paragraph"
            className="text-lg text-coarse-wool text-left font-bold mb-12 lg:hidden"
          >
            Select cancellation reasons
          </Text>
          {/* <Icon type="angle-down" onClick={handleClose} className="lg:hidden cursor-pointer" /> */}
        </div>

        {/* TODO: will implement later */}
        {/* Search Bar */}
        {/* <SearchBarV2
          searchPlaceholder={`Search ${name}`}
          className="h-[40px] mb-16"
          ref={searchBarRef}
          value={searchTerm}
          onSearch={handleSearch}
        /> */}

        <div className="block lg:hidden border-b border-aquarelle-purple" />

        <div className="h-full max-h-[320px] overflow-y-auto custom-scrollbar-store">
          {options?.map((item, index) => {
            const isLast = index === options.length - 1;

            return (
              <div
                key={item.id}
                className={cn(
                  'flex items-center justify-between px-8 py-12 lg:py-16 cursor-pointer border-b border-coconut-agony',
                  isLast && '!border-none',
                )}
                onClick={() => handleSelectOption(item)}
              >
                <div className="flex gap-4 items-center">
                  <div className="min-w-[20px] min-h-[20px] flex justify-center items-center">
                    {selectedOption?.id === item.id ? (
                      <Icon type="radio-active" className="size-[20px]" />
                    ) : (
                      <Icon type="radio" className="size-[20px] " />
                    )}
                  </div>
                  <Text
                    type="paragraph"
                    className={cn(
                      '!text-14 text-gunmetal font-encode-sans font-medium capitalize leading-20 tracking-[0.28px]',
                      {
                        'text-richcarmine !font-bold':
                          selectedOption?.id === item.id,
                      },
                    )}
                  >
                    {item?.name[selectLang] ?? item?.name[DEFAULT_LANGUAGE]}
                  </Text>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default DropdownOptionsData;
